import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "@/components/Logo";
import { scrollToSection } from "@/lib/scroll";

const LINKS = [
  { label: "The Man", hash: "#about", testid: "nav-link-about" },
  { label: "Record", hash: "#record", testid: "nav-link-record" },
  { label: "Westlands", hash: "#westlands", testid: "nav-link-westlands" },
  { label: "Movement", hash: "#movement", testid: "nav-link-movement" },
  { label: "Vision 2027", hash: "#vision", testid: "nav-link-vision" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (hash) => {
    setOpen(false);
    scrollToSection(hash);
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "glass" : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto flex items-center justify-between px-5 sm:px-8 h-[72px]">
        <button
          data-testid="nav-logo-btn"
          onClick={() => go("#top")}
          className="flex items-center gap-3 group"
        >
          <Logo />
          <span className="text-left leading-tight">
            <span className="font-display font-bold tracking-wide block text-sm sm:text-base">
              TIM WANYONYI
            </span>
            <span className="mono text-[10px] tracking-[0.3em] text-orange-500 block">
              BUNGOMA 2027
            </span>
          </span>
        </button>

        <div className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.hash}
              data-testid={l.testid}
              onClick={() => go(l.hash)}
              className="mono text-xs tracking-[0.18em] uppercase text-stone-400 hover:text-orange-400 transition-colors duration-200"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-join-btn"
            onClick={() => go("#join")}
            className="group flex items-center gap-2 rounded-full bg-orange-600 hover:bg-orange-500 px-5 py-2.5 mono text-xs font-semibold tracking-[0.15em] uppercase text-[#0B0C10] transition-colors duration-200"
          >
            Join Campaign
            <ArrowUpRight size={14} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden text-stone-200 p-2"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="lg:hidden glass overflow-hidden"
          >
            <div className="flex flex-col px-6 py-4 gap-1">
              {LINKS.map((l) => (
                <button
                  key={l.hash}
                  data-testid={`${l.testid}-mobile`}
                  onClick={() => go(l.hash)}
                  className="text-left py-3 mono text-sm tracking-[0.18em] uppercase text-stone-300 border-b border-white/5"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="nav-join-btn-mobile"
                onClick={() => go("#join")}
                className="mt-3 mb-2 rounded-full bg-orange-600 px-5 py-3 mono text-xs font-semibold tracking-[0.15em] uppercase text-[#0B0C10]"
              >
                Join Campaign
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
